import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

const PERSIAN_DAYS = [
  { name: 'شنبه', key: 'sat' },
  { name: 'یکشنبه', key: 'sun' },
  { name: 'دوشنبه', key: 'mon' },
  { name: 'سه‌شنبه', key: 'tue' },
  { name: 'چهارشنبه', key: 'wed' },
  { name: 'پنجشنبه', key: 'thu' },
  { name: 'جمعه', key: 'fri' },
];

// Seeded pseudo-random based on user id string — range 950-1100 (thousand toman)
function seededDailyIncome(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  const val = Math.abs(hash % 151) + 950; // 950-1100
  return val * 1000; // convert to toman
}

// Generate 7 days of activity data (pseudo-random based on user id + day index)
function generateWeeklyActivity(seed: string): Array<{ day: string; value: number; isToday: boolean }> {
  const data = [];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash = hash & hash;
  }
  for (let i = 0; i < 7; i++) {
    const dayHash = Math.abs((hash * (i + 1) * 7 + i * 13) % 100);
    const value = 60 + (dayHash % 41); // 60-100 range
    data.push({ day: PERSIAN_DAYS[i].name, value, isToday: false });
  }
  return data;
}

export default function MemberPanel() {
  const { user, profile, loading, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'dashboard' | 'messages' | 'config'>('dashboard');

  // Messages
  const [messages, setMessages] = useState<Array<{
    seq_id: number;
    sender_id: string;
    receiver_id: string;
    content: string;
    is_read: boolean;
    created_at: string;
  }>>([]);
  const [newMessage, setNewMessage] = useState('');
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgSending, setMsgSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Config
  const [netConfig, setNetConfig] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);

  // Animated greeting
  const [greetingShown, setGreetingShown] = useState(false);

  // Auto-refresh timer
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!loading && user && profile && isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [user, loading, isAdmin, profile, navigate]);

  useEffect(() => {
    const t = setTimeout(() => setGreetingShown(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Load messages via Edge Function
  const loadMessages = useCallback(async () => {
    if (!user) return;
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/user-messages?action=get-messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const json = await res.json();
        if (json.messages) setMessages(json.messages);
      }
    } catch (_) { /* silent */ }
  }, [user]);

  const loadConfig = useCallback(async () => {
    const { data } = await supabase
      .from('configs')
      .select('value')
      .eq('key', 'net_national_config')
      .maybeSingle();
    if (data) setNetConfig(data.value || '');
    setConfigLoaded(true);
  }, []);

  useEffect(() => {
    if (user) {
      loadMessages();
      loadConfig();
    }
  }, [user, loadMessages, loadConfig]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => loadMessages(), 5000);
    return () => clearInterval(interval);
  }, [user, loadMessages]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !user || msgSending) return;
    setMsgSending(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;

      const res = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/user-messages?action=send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ content: newMessage.trim() }),
        }
      );
      if (res.ok) {
        setNewMessage('');
        await loadMessages();
      } else {
        const errData = await res.json().catch(() => null);
        console.error('Send failed:', errData);
      }
    } catch (e) {
      console.error('Send error:', e);
    }
    setMsgSending(false);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  // ─── COMPUTED VALUES ───────────────────────────────

  const daysSinceJoin = useMemo(() => {
    if (!profile?.created_at) return 0;
    const created = new Date(profile.created_at);
    const diffMs = now.getTime() - created.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }, [profile?.created_at, now]);

  const joinDate = useMemo(() => {
    if (!profile?.created_at) return '';
    return new Date(profile.created_at).toLocaleDateString('fa-IR', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  }, [profile?.created_at]);

  // Daily income: 950K-1.1M based on seed from user id
  const dailyIncome = useMemo(() => {
    if (!user) return 950000;
    return seededDailyIncome(user.id);
  }, [user]);

  // Monthly income WITHOUT overtime/holidays (22 working days)
  const monthlyBaseIncome = useMemo(() => dailyIncome * 22, [dailyIncome]);

  // Monthly income WITH overtime + holidays (30 days)
  const monthlyFullIncome = useMemo(() => dailyIncome * 30, [dailyIncome]);

  // Weekly activity data
  const weeklyActivity = useMemo(() => {
    if (!user) return [];
    const data = generateWeeklyActivity(user.id);
    const todayIdx = (now.getDay() + 1) % 7;
    if (data[todayIdx]) data[todayIdx].isToday = true;
    return data;
  }, [user, now]);

  const membershipLevel = useMemo(() => {
    if (daysSinceJoin < 7) return { label: 'تازه‌وارد', color: 'from-emerald-400 to-teal-500', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'ri-seedling-line', percent: Math.min(100, (daysSinceJoin / 7) * 100) };
    if (daysSinceJoin < 30) return { label: 'فعال', color: 'from-amber-400 to-orange-500', bg: 'bg-amber-50', text: 'text-amber-700', icon: 'ri-fire-line', percent: Math.min(100, ((daysSinceJoin - 7) / 23) * 100) };
    if (daysSinceJoin < 90) return { label: 'حرفه‌ای', color: 'from-rose-400 to-pink-500', bg: 'bg-rose-50', text: 'text-rose-700', icon: 'ri-star-line', percent: Math.min(100, ((daysSinceJoin - 30) / 60) * 100) };
    return { label: 'کهنه‌کار', color: 'from-violet-400 to-purple-500', bg: 'bg-violet-50', text: 'text-violet-700', icon: 'ri-vip-crown-line', percent: 100 };
  }, [daysSinceJoin]);

  const chatMessages = messages.filter(m =>
    m.sender_id === user?.id || m.receiver_id === user?.id
  );
  const unreadCount = messages.filter(m => m.receiver_id === user?.id && !m.is_read).length;

  const shiftLabel = profile?.shift_type === '12-17' ? 'شیفت ظهر' : 'شیفت شب';
  const shiftIcon = profile?.shift_type === '12-17' ? 'ri-sun-line' : 'ri-moon-line';
  const shiftHours = profile?.shift_type === '12-17' ? '۱۲:۰۰ - ۱۷:۰۰' : '۱۸:۳۰ - ۲۲:۰۰';
  const isShiftDay = profile?.shift_type === '12-17';

  const todayPersianIndex = useMemo(() => (now.getDay() + 1) % 7, [now]);
  const isWorkingDay = todayPersianIndex < 5;

  const circleR = 40;
  const circleC = 2 * Math.PI * circleR;

  // ─── LOADING STATE ─────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mx-auto mb-6">
            <div className="w-20 h-20 rounded-full border-[3px] border-stone-200 border-t-stone-800 animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center">
                <span className="text-white font-pacifico text-sm">C</span>
              </div>
            </div>
          </div>
          <p className="text-stone-400 text-sm font-medium">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative mx-auto mb-6">
            <div className="w-20 h-20 rounded-full border-[3px] border-stone-200 border-t-stone-800 animate-spin"></div>
          </div>
          <p className="text-stone-400 text-sm font-medium">در حال احراز هویت...</p>
        </div>
      </div>
    );
  }

  const maxActivityValue = Math.max(...weeklyActivity.map(d => d.value), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/20 to-stone-50">
      {/* ─── GLASS HEADER ──────────────────────────── */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-stone-200/50">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center gap-2.5 cursor-pointer group">
              <div className="w-9 h-9 rounded-xl bg-stone-800 flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                <span className="text-white font-pacifico text-sm">C</span>
              </div>
              <span className="text-base font-black text-stone-800 hidden sm:block">کارلند</span>
            </a>

            {/* Tabs Desktop */}
            <div className="hidden sm:flex items-center bg-stone-100 rounded-full p-1 gap-0.5">
              {[
                { key: 'dashboard', icon: 'ri-dashboard-3-line', label: 'داشبورد' },
                { key: 'messages', icon: 'ri-chat-smile-2-line', label: 'پشتیبانی', badge: unreadCount },
                { key: 'config', icon: 'ri-code-s-slash-line', label: 'کانفینگ' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap relative ${
                    activeTab === tab.key ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  {tab.label}
                  {tab.badge && tab.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-bounce-in">{tab.badge}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Tabs Mobile */}
            <div className="sm:hidden flex items-center gap-1">
              {[
                { key: 'dashboard', icon: 'ri-dashboard-3-line', badge: 0 },
                { key: 'messages', icon: 'ri-chat-smile-2-line', badge: unreadCount },
                { key: 'config', icon: 'ri-code-s-slash-line', badge: 0 },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all relative ${
                    activeTab === tab.key ? 'bg-stone-800 text-white' : 'text-stone-500'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.badge > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{tab.badge}</span>}
                </button>
              ))}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-stone-100">
                <span className={`w-2 h-2 rounded-full ${isWorkingDay ? 'bg-green-500 animate-pulse' : 'bg-stone-400'}`}></span>
                <span className="text-xs text-stone-500">{isWorkingDay ? 'امروز کاری' : 'امروز تعطیل'}</span>
              </div>
              <button onClick={handleLogout} className="flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-medium text-red-500 hover:bg-red-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-logout-box-r-line"></i>
                <span className="hidden sm:inline">خروج</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ─── DASHBOARD TAB ──────────────────────────── */}
      {activeTab === 'dashboard' && (
        <main className="py-6 md:py-8">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto space-y-5 md:space-y-6">

              {/* ─── HERO PROFILE CARD ─────────────── */}
              <div className={`relative overflow-hidden rounded-3xl bg-white border border-stone-100 p-6 md:p-8 transition-all duration-700 ${greetingShown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-100/40 blur-3xl"></div>
                <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-rose-100/30 blur-3xl"></div>
                <div className="absolute top-1/2 left-1/3 w-40 h-40 rounded-full bg-emerald-100/20 blur-3xl"></div>

                <div className="relative flex flex-col md:flex-row items-start md:items-center gap-5">
                  {/* Avatar section */}
                  <div className="relative flex-shrink-0">
                    <div className="relative">
                      <svg className="absolute -inset-3 w-[88px] h-[88px] -rotate-90" viewBox="0 0 88 88">
                        <circle cx="44" cy="44" r="40" fill="none" stroke="#e7e5e4" strokeWidth="4" />
                        <circle
                          cx="44" cy="44" r="40" fill="none"
                          stroke={`url(#gradient-${profile.username})`}
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeDasharray={circleC}
                          strokeDashoffset={circleC * (1 - membershipLevel.percent / 100)}
                          className="transition-all duration-1000"
                        />
                        <defs>
                          <linearGradient id={`gradient-${profile.username}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#d6a85c" />
                            <stop offset="100%" stopColor="#c49b3f" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-stone-700 to-stone-900 flex items-center justify-center shadow-lg shadow-stone-900/15">
                        <span className="text-white text-xl font-black">{profile.full_name?.charAt(0) || 'ک'}</span>
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-[3px] border-white bg-green-500 flex items-center justify-center">
                        <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                      </div>
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white rounded-full px-3 py-0.5 border border-stone-200 shadow-sm">
                      <span className="text-[10px] font-black text-stone-700 whitespace-nowrap">{daysSinceJoin} روز</span>
                    </div>
                  </div>

                  {/* Profile Info */}
                  <div className="flex-1 text-right">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h1 className="text-xl md:text-2xl font-black text-stone-800">{profile.full_name || 'کاربر گرامی'}</h1>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r ${membershipLevel.color} text-white`}>
                        {membershipLevel.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-stone-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-at-line"></i>
                        <span className="font-mono" dir="ltr">@{profile.username}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i>
                        عضویت از {joinDate}
                      </span>
                      <span className={`flex items-center gap-1 ${profile.is_verified ? 'text-green-600' : 'text-amber-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${profile.is_verified ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                        {profile.is_verified ? 'احراز شده' : 'در انتظار احراز'}
                      </span>
                      {profile.job_category && (
                        <span className={`flex items-center gap-1 ${profile.job_category === 'advertising' ? 'text-rose-600' : 'text-emerald-600'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${profile.job_category === 'advertising' ? 'bg-rose-500' : 'bg-emerald-500'}`}></span>
                          حوزه: {profile.job_category === 'advertising' ? 'تبلیغات' : 'تریدینگ'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Big Progress Circle */}
                  <div className="flex-shrink-0 hidden md:flex flex-col items-center gap-1.5">
                    <div className="relative w-28 h-28">
                      <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                        <circle cx="50" cy="50" r="42" fill="none" stroke="#f5f0eb" strokeWidth="6" />
                        <circle cx="50" cy="50" r="42" fill="none" stroke={`url(#bigGrad-${profile.username})`} strokeWidth="6" strokeLinecap="round"
                          strokeDasharray={2 * Math.PI * 42}
                          strokeDashoffset={2 * Math.PI * 42 * (1 - membershipLevel.percent / 100)}
                          className="transition-all duration-1000 ease-out" />
                        <defs>
                          <linearGradient id={`bigGrad-${profile.username}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#d6a85c" />
                            <stop offset="100%" stopColor="#b8860b" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-3xl font-black text-stone-800">{daysSinceJoin}</span>
                        <span className="text-[10px] text-stone-400">روز فعالیت</span>
                      </div>
                    </div>
                    <span className="text-[10px] text-stone-400">تا سطح {membershipLevel.label}</span>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="relative mt-5 pt-4 border-t border-stone-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-stone-500 flex items-center gap-1">
                      <i className={`${membershipLevel.icon} text-xs`}></i>
                      پیشرفت به سطح بعدی
                    </span>
                    <span className="text-[11px] font-semibold text-stone-400">{Math.round(membershipLevel.percent)}%</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-stone-100 overflow-hidden p-0.5">
                    <div className={`h-full rounded-full bg-gradient-to-r ${membershipLevel.color} transition-all duration-700 ease-out relative`}
                      style={{ width: `${membershipLevel.percent}%` }}>
                      <div className="absolute inset-0 bg-white/20 rounded-full animate-shimmer"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── EARNINGS CARDS ──────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Daily Income */}
                <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 p-5 hover:border-emerald-200 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full bg-gradient-to-bl from-emerald-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-money-dollar-circle-line text-lg text-emerald-700"></i>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <p className="text-xl font-black text-stone-800">{dailyIncome.toLocaleString()}</p>
                      <span className="text-xs text-stone-400">تومان</span>
                    </div>
                    <p className="text-[11px] font-semibold text-stone-600 mt-2 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      درآمد روزانه
                    </p>
                  </div>
                </div>

                {/* Monthly Base Income (22 days, no overtime) */}
                <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 p-5 hover:border-amber-200 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full bg-gradient-to-bl from-amber-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-funds-line text-lg text-amber-700"></i>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <p className="text-xl font-black text-stone-800">{monthlyBaseIncome.toLocaleString()}</p>
                      <span className="text-xs text-stone-400">تومان</span>
                    </div>
                    <p className="text-[11px] font-semibold text-stone-600 mt-2 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                      درآمد ماهانه (۲۲ روز کاری)
                    </p>
                  </div>
                </div>

                {/* Monthly Full Income (30 days, with overtime) */}
                <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 p-5 hover:border-rose-200 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full bg-gradient-to-bl from-rose-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-100 to-rose-200 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                      <i className="ri-bank-line text-lg text-rose-700"></i>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <p className="text-xl font-black text-stone-800">{monthlyFullIncome.toLocaleString()}</p>
                      <span className="text-xs text-stone-400">تومان</span>
                    </div>
                    <p className="text-[11px] font-semibold text-stone-600 mt-2 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                      با احتساب اضافه‌کاری (۳۰ روز)
                    </p>
                  </div>
                </div>

                {/* Shift Card */}
                <div className="group relative overflow-hidden rounded-2xl bg-white border border-stone-100 p-5 hover:border-stone-300 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full bg-gradient-to-bl from-stone-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300 ${isShiftDay ? 'bg-gradient-to-br from-amber-100 to-amber-200' : 'bg-gradient-to-br from-indigo-100 to-indigo-200'}`}>
                      <i className={`text-lg ${isShiftDay ? 'ri-sun-line text-amber-700' : 'ri-moon-line text-indigo-700'}`}></i>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <p className={`text-sm font-black ${isShiftDay ? 'text-amber-700' : 'text-indigo-700'}`} dir="ltr">{shiftHours}</p>
                    </div>
                    <p className="text-[11px] font-semibold text-stone-600 mt-2 flex items-center gap-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${isShiftDay ? 'bg-amber-500' : 'bg-indigo-500'}`}></span>
                      {shiftLabel}
                    </p>
                  </div>
                </div>
              </div>

              {/* ─── WEEKLY PERFORMANCE CHART ────────── */}
              <div className="rounded-2xl bg-white border border-stone-100 p-5 md:p-6 animate-fade-in-up" style={{ animationDelay: '0.45s' }}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-base font-black text-stone-800">عملکرد هفتگی</h2>
                    <p className="text-[11px] text-stone-400 mt-0.5">میزان فعالیت روزانه شما در هفته جاری</p>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-stone-400">
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm bg-stone-800"></span>
                      روز جاری
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-sm bg-stone-200"></span>
                      سایر روزها
                    </span>
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="flex items-end justify-between gap-1 md:gap-2 h-48 md:h-56">
                  {weeklyActivity.map((item, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                      {/* Value label */}
                      <span className={`text-[10px] font-bold ${item.isToday ? 'text-stone-800' : 'text-stone-400'}`}>
                        {item.value}%
                      </span>
                      {/* Bar */}
                      <div
                        className={`w-full max-w-[48px] rounded-t-xl transition-all duration-500 relative ${
                          item.isToday
                            ? 'bg-gradient-to-t from-stone-700 to-stone-800 shadow-lg shadow-stone-800/20'
                            : 'bg-gradient-to-t from-stone-200 to-stone-300'
                        }`}
                        style={{ height: `${(item.value / maxActivityValue) * 75}%` }}
                      >
                        {item.isToday && (
                          <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-stone-800 text-white text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                            امروز
                          </div>
                        )}
                      </div>
                      {/* Day label */}
                      <span className={`text-[10px] font-semibold mt-1 ${item.isToday ? 'text-stone-800' : 'text-stone-400'}`}>
                        {item.day}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3 mt-6 pt-5 border-t border-stone-100">
                  <div className="text-center">
                    <p className="text-[10px] text-stone-400 mb-1">میانگین هفتگی</p>
                    <p className="text-sm font-black text-stone-800">
                      {Math.round(weeklyActivity.reduce((sum, d) => sum + d.value, 0) / weeklyActivity.length)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-stone-400 mb-1">بیشترین</p>
                    <p className="text-sm font-black text-emerald-600">
                      {Math.max(...weeklyActivity.map(d => d.value))}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-[10px] text-stone-400 mb-1">روزهای فعال</p>
                    <p className="text-sm font-black text-stone-800">
                      {weeklyActivity.filter(d => d.value >= 80).length} از ۷
                    </p>
                  </div>
                </div>
              </div>

              {/* ─── WORK SCHEDULE + TODAY STATUS ────── */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Week Calendar */}
                <div className="lg:col-span-2 rounded-2xl bg-white border border-stone-100 p-5 md:p-6 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-base font-black text-stone-800">تقویم هفتگی</h2>
                      <p className="text-[11px] text-stone-400 mt-0.5">برنامه شیفت کاری شما</p>
                    </div>
                    <span className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold border ${isShiftDay ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-indigo-50 border-indigo-200 text-indigo-700'}`}>
                      <i className={shiftIcon}></i>
                      {shiftLabel}
                    </span>
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {PERSIAN_DAYS.map((day, idx) => {
                      const isToday = idx === todayPersianIndex;
                      const isOptional = idx >= 5;
                      return (
                        <div key={day.key} className={`relative rounded-xl p-3 text-center transition-all duration-300 ${
                          isToday
                            ? 'bg-stone-800 text-white shadow-lg shadow-stone-800/20 scale-105 z-10'
                            : isOptional
                              ? 'bg-stone-50/80 text-stone-400'
                              : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                        }`}>
                          {isToday && (
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold whitespace-nowrap shadow-md">
                              امروز
                            </div>
                          )}
                          <p className="text-[11px] font-semibold mb-2">{day.name}</p>
                          <div className={`w-9 h-9 rounded-full mx-auto flex items-center justify-center transition-all ${
                            isToday ? 'bg-white/20' : isOptional ? 'bg-stone-200/50' : 'bg-white shadow-sm'
                          }`}>
                            {isOptional ? (
                              <i className={`ri-question-line text-sm ${isToday ? 'text-white/70' : 'text-stone-300'}`}></i>
                            ) : isToday ? (
                              <i className={`${shiftIcon} text-sm text-white`}></i>
                            ) : (
                              <i className={`${shiftIcon} text-sm ${isShiftDay ? 'text-amber-500' : 'text-indigo-500'}`}></i>
                            )}
                          </div>
                          <p className={`text-[10px] mt-1.5 font-medium ${isToday ? 'text-white/80' : ''}`}>
                            {isOptional ? 'اختیاری' : shiftHours}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 p-3.5 rounded-xl bg-stone-50 text-right border border-stone-100">
                    <p className="text-[11px] text-stone-500 leading-relaxed">
                      <i className="ri-information-line ml-1 text-stone-400"></i>
                      <strong className="text-stone-700">شنبه تا چهارشنبه</strong> روزهای کاری اصلی • <strong className="text-stone-700">پنجشنبه و جمعه</strong> اختیاری
                    </p>
                  </div>
                </div>

                {/* Today's Status Card */}
                <div className="rounded-2xl bg-white border border-stone-100 p-5 md:p-6 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                  <h2 className="text-base font-black text-stone-800 mb-4 flex items-center gap-2">
                    <i className="ri-calendar-todo-line text-amber-500"></i>
                    وضعیت امروز
                  </h2>

                  <div className="space-y-4">
                    {/* Day Status */}
                    <div className={`rounded-xl p-4 ${isWorkingDay ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100' : 'bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-100'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isWorkingDay ? 'bg-emerald-200' : 'bg-stone-200'}`}>
                          <i className={`text-lg ${isWorkingDay ? 'ri-briefcase-line text-emerald-700' : 'ri-moon-line text-stone-500'}`}></i>
                        </div>
                        <div className="text-right flex-1">
                          <p className={`text-sm font-bold ${isWorkingDay ? 'text-emerald-800' : 'text-stone-600'}`}>
                            {isWorkingDay ? 'روز کاری' : 'روز تعطیل'}
                          </p>
                          <p className="text-[11px] text-stone-400">{PERSIAN_DAYS[todayPersianIndex].name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Today Earnings */}
                    <div className="rounded-xl p-4 bg-gradient-to-br from-stone-50 to-amber-50/50 border border-stone-100">
                      <div className="flex items-center justify-between mb-1">
                        <i className="ri-coins-line text-amber-400"></i>
                        <p className="text-[11px] text-stone-400">درآمد احتمالی امروز</p>
                      </div>
                      <p className="text-xl font-black text-stone-800 text-right mt-1.5">
                        {isWorkingDay ? `${dailyIncome.toLocaleString()} تومان` : '—'}
                      </p>
                      <p className="text-[10px] text-stone-400 mt-1 text-right">بر اساس میانگین سیگنال‌های ارسالی</p>
                    </div>

                    {/* Quick Stats */}
                    <div className="rounded-xl bg-stone-50 border border-stone-100 p-4 space-y-3">
                      {[
                        { icon: 'ri-calendar-check-line', label: 'روزهای عضویت', value: `${daysSinceJoin} روز`, color: 'text-amber-600' },
                        { icon: membershipLevel.icon, label: 'سطح عضویت', value: membershipLevel.label, color: membershipLevel.text },
                        { icon: 'ri-shield-check-line', label: 'وضعیت حساب', value: profile.is_verified ? 'فعال' : 'در انتظار', color: profile.is_verified ? 'text-emerald-600' : 'text-amber-600' },
                        { icon: 'ri-signal-tower-line', label: 'کارمزد هر سیگنال', value: `${(profile?.commission_per_signal || 450000).toLocaleString()} تومان`, color: 'text-stone-700' },
                      { icon: profile?.job_category === 'advertising' ? 'ri-advertisement-line' : 'ri-line-chart-line', label: 'حوزه کاری', value: profile?.job_category === 'advertising' ? 'تبلیغات' : 'تریدینگ', color: profile?.job_category === 'advertising' ? 'text-rose-600' : 'text-emerald-600' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-[11px] text-stone-400 flex items-center gap-1.5">
                            <i className={`${item.icon} text-xs`}></i>
                            {item.label}
                          </span>
                          <span className={`text-xs font-bold ${item.color}`}>{item.value}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => setActiveTab('messages')}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-stone-800 text-white text-sm font-semibold hover:bg-stone-900 transition-all cursor-pointer whitespace-nowrap active:scale-[0.98]"
                    >
                      <i className="ri-chat-smile-2-line"></i>
                      ارتباط با پشتیبانی
                    </button>
                  </div>
                </div>
              </div>

              {/* ─── QUICK STATS ROW ──────────────────── */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in-up" style={{ animationDelay: '0.7s' }}>
                {[
                  { icon: 'ri-shield-check-line', label: 'احراز هویت', value: profile.is_verified ? 'تایید شده' : 'در انتظار', color: profile.is_verified ? 'text-emerald-600' : 'text-amber-600', bg: profile.is_verified ? 'bg-emerald-100' : 'bg-amber-100', ic: profile.is_verified ? 'text-emerald-600' : 'text-amber-600' },
                  { icon: 'ri-time-line', label: 'ساعت کاری', value: shiftHours, ic: 'text-stone-600', bg: 'bg-stone-100' },
                  { icon: 'ri-user-star-line', label: 'سطح کاربری', value: membershipLevel.label, ic: membershipLevel.text, bg: 'bg-amber-100' },
                  { icon: profile?.job_category === 'advertising' ? 'ri-advertisement-line' : 'ri-line-chart-line', label: 'حوزه کاری', value: profile?.job_category === 'advertising' ? 'تبلیغات' : 'تریدینگ', ic: profile?.job_category === 'advertising' ? 'text-rose-600' : 'text-emerald-600', bg: profile?.job_category === 'advertising' ? 'bg-rose-100' : 'bg-emerald-100' },
                  { icon: 'ri-money-dollar-circle-line', label: 'درآمد پایه ماه', value: `${(monthlyBaseIncome / 1000000).toFixed(1)}M`, ic: 'text-stone-600', bg: 'bg-stone-100' },
                ].map((item, i) => (
                  <div key={i} className="rounded-xl bg-white border border-stone-100 p-4 flex items-center gap-3 hover:border-stone-200 transition-all duration-200">
                    <div className={`w-10 h-10 rounded-xl ${item.bg} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${item.icon} ${item.ic} text-base`}></i>
                    </div>
                    <div className="text-right min-w-0">
                      <p className="text-[11px] text-stone-400 truncate">{item.label}</p>
                      <p className={`text-sm font-bold truncate ${item.ic}`}>{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ─── CONFIG CTA ──────────────────────── */}
              <div className="animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-stone-800 via-stone-900 to-stone-950 p-6 md:p-8 text-white">
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl"></div>
                  <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full bg-amber-500/10 blur-3xl"></div>
                  <div className="absolute top-1/2 right-1/3 w-32 h-32 rounded-full bg-teal-400/5 blur-3xl"></div>

                  <div className="relative flex flex-col md:flex-row items-start md:items-center gap-5">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center">
                          <i className="ri-code-s-slash-line text-2xl md:text-3xl text-emerald-400"></i>
                        </div>
                        <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                          <i className="ri-download-2-line text-white text-xs"></i>
                        </div>
                      </div>
                    </div>

                    {/* Text */}
                    <div className="flex-1 text-right">
                      <h3 className="text-lg md:text-xl font-black mb-2">کانفینگ اختصاصی نت ملی</h3>
                      <p className="text-sm text-stone-400 leading-relaxed max-w-xl">
                        تنظیمات اتصال اختصاصی شما برای استفاده از سرویس نت ملی. این کانفینگ مخصوص حساب کاربری شماست و برای اتصال پایدار و ایمن طراحی شده است.
                      </p>
                      {netConfig && (
                        <div className="mt-3 flex items-center gap-2 justify-end">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                          <span className="text-xs text-emerald-400 font-medium">کانفینگ فعال است</span>
                        </div>
                      )}
                    </div>

                    {/* Button */}
                    <button
                      onClick={() => setActiveTab('config')}
                      className="flex-shrink-0 w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold text-sm hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl shadow-emerald-500/20 active:scale-[0.98]"
                    >
                      <i className="ri-download-2-line text-lg"></i>
                      دریافت کانفینگ اختصاصی
                    </button>
                  </div>

                  {/* Features row */}
                  <div className="relative mt-5 pt-5 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                        <i className="ri-shield-check-line text-emerald-400 text-sm"></i>
                      </div>
                      <span className="text-xs text-stone-400">اتصال ایمن و رمزنگاری شده</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <i className="ri-speed-up-line text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-xs text-stone-400">سرعت بالا بدون قطعی</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                        <i className="ri-user-line text-teal-400 text-sm"></i>
                      </div>
                      <span className="text-xs text-stone-400">مخصوص حساب کاربری شما</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </main>
      )}

      {/* ─── MESSAGES TAB ──────────────────────────── */}
      {activeTab === 'messages' && (
        <main className="py-6 md:py-8">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <div className="rounded-2xl bg-white border border-stone-100 overflow-hidden animate-fade-in-up">
                {/* Chat Header */}
                <div className="p-5 border-b border-stone-100 bg-gradient-to-r from-stone-50 via-white to-stone-50">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                        <i className="ri-headphone-line text-white text-lg"></i>
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-[3px] border-white bg-emerald-500"></div>
                    </div>
                    <div className="text-right flex-1">
                      <p className="text-sm font-black text-stone-800">پشتیبانی کارلند</p>
                      <p className="text-[11px] text-emerald-600 font-medium flex items-center gap-1 justify-end">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                        آنلاین • پاسخگویی سریع
                      </p>
                    </div>
                    <span className="text-[10px] text-stone-400 bg-stone-100 px-2.5 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-shield-check-line text-emerald-500"></i>
                      رسمی
                    </span>
                  </div>
                </div>

                {/* Messages Area */}
                <div className="p-5 h-[420px] md:h-[500px] overflow-y-auto space-y-3 bg-gradient-to-b from-stone-50/50 to-white" id="chat-messages">
                  {chatMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <div className="w-20 h-20 rounded-full bg-stone-100 flex items-center justify-center mb-4">
                        <i className="ri-chat-smile-2-line text-2xl text-stone-300"></i>
                      </div>
                      <p className="text-sm font-bold text-stone-500 mb-1">گفتگویی شروع نشده</p>
                      <p className="text-xs text-stone-400 max-w-[280px] leading-relaxed">
                        سوالات، مشکلات یا درخواست‌های خود را مطرح کنید. تیم پشتیبانی در اسرع وقت پاسخ می‌دهد.
                      </p>
                    </div>
                  ) : (
                    <>
                      {chatMessages.length > 0 && chatMessages.every(m => m.sender_id === user.id) && (
                        <div className="flex justify-start animate-fade-in-up">
                          <div className="max-w-[75%] rounded-2xl rounded-bl-md px-4 py-3 bg-emerald-50 border border-emerald-100">
                            <p className="text-xs text-emerald-800">پیام شما دریافت شد. پشتیبانی به زودی پاسخ می‌دهد.</p>
                            <p className="text-[10px] text-emerald-500 mt-1.5 flex items-center gap-1">
                              <i className="ri-headphone-line"></i>
                              پشتیبانی کارلند
                            </p>
                          </div>
                        </div>
                      )}
                      {chatMessages.map(msg => (
                        <div
                          key={msg.seq_id}
                          className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'} animate-fade-in-up`}
                        >
                          {msg.sender_id !== user.id && (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 ml-2 mt-1">
                              <i className="ri-headphone-line text-white text-[11px]"></i>
                            </div>
                          )}
                          <div className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                            msg.sender_id === user.id
                              ? 'bg-stone-800 text-white rounded-br-md shadow-md'
                              : 'bg-white border border-stone-100 text-stone-700 rounded-bl-md shadow-sm'
                          }`}>
                            <p className="text-xs leading-relaxed">{msg.content}</p>
                            <p className={`text-[10px] mt-1.5 flex items-center gap-1 ${msg.sender_id === user.id ? 'text-white/50' : 'text-stone-400'}`}>
                              {msg.sender_id === user.id && <i className="ri-check-double-line text-[10px] text-emerald-400"></i>}
                              {new Date(msg.created_at).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef}></div>
                    </>
                  )}
                </div>

                {/* Input Area */}
                <div className="p-4 border-t border-stone-100 bg-white">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !msgSending) handleSendMessage(); }}
                      placeholder="پیام خود را بنویسید..."
                      className="flex-1 pl-4 pr-4 py-3 rounded-xl bg-stone-50 border border-stone-100 text-xs text-right focus:outline-none focus:bg-white focus:border-stone-300 transition-all placeholder:text-stone-300"
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={msgSending || !newMessage.trim()}
                      className="w-11 h-11 rounded-xl bg-stone-800 text-white flex items-center justify-center hover:bg-stone-900 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0 active:scale-95"
                    >
                      {msgSending ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      ) : (
                        <i className="ri-send-plane-fill"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      )}

      {/* ─── CONFIG TAB ──────────────────────────── */}
      {activeTab === 'config' && (
        <main className="py-6 md:py-8">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <div className="rounded-2xl bg-white border border-stone-100 overflow-hidden animate-fade-in-up">
                <div className="p-5 md:p-6 border-b border-stone-100 bg-gradient-to-r from-stone-50 to-white">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-11 h-11 rounded-xl bg-stone-800 flex items-center justify-center shadow-lg shadow-stone-800/10">
                      <i className="ri-code-s-slash-line text-white text-lg"></i>
                    </div>
                    <div className="text-right">
                      <h2 className="text-base font-black text-stone-800">کانفینگ نت ملی</h2>
                      <p className="text-[11px] text-stone-400">تنظیمات اختصاصی اتصال شبکه</p>
                    </div>
                  </div>
                </div>

                <div className="p-5 md:p-6">
                  {!configLoaded ? (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 rounded-full border-[3px] border-stone-200 border-t-stone-800 animate-spin mx-auto mb-4"></div>
                      <p className="text-stone-400 text-sm">در حال دریافت کانفینگ...</p>
                    </div>
                  ) : !netConfig ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                        <i className="ri-information-line text-2xl text-amber-500"></i>
                      </div>
                      <p className="text-stone-700 font-bold text-sm">کانفینگی تنظیم نشده</p>
                      <p className="text-stone-400 text-xs mt-1">لطفاً با پشتیبانی تماس بگیرید.</p>
                    </div>
                  ) : (
                    <div className="mb-5">
                      <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200 font-mono text-xs text-stone-700 leading-relaxed whitespace-pre-wrap text-left select-all" dir="ltr">
                        {netConfig}
                      </div>
                      <div className="flex items-center justify-end gap-2 mt-2">
                        <button
                          onClick={() => { navigator.clipboard.writeText(netConfig); }}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-stone-100 text-stone-500 text-[11px] font-medium hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-file-copy-line text-xs"></i>
                          کپی
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-right">
                    <div className="flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-alert-line text-red-500"></i>
                      </div>
                      <div>
                        <p className="text-red-600 text-sm font-bold mb-1">هشدار مهم</p>
                        <p className="text-red-500 text-xs leading-relaxed">
                          این کانفینگ مخصوص نت ملی طراحی شده است. در نظر داشته باشید این سرویس تنها مختص کارتونه و در صورت استفاده شخصی و غیرمجاز، اتصال شما قطع و امکان ثبت کانفینگ جدید برای شما فراهم نخواهد شد.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 text-center">
                    <button onClick={loadConfig} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-stone-100 text-stone-600 text-xs font-medium hover:bg-stone-200 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-refresh-line"></i>
                      بروزرسانی کانفینگ
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );
}